/**
 * useKiCockpit — Hook für das KI-Cockpit
 * Liest und schreibt Bereiche, Einträge, Notizen und Fragen
 * aus den ki_cockpit_bereiche / ki_cockpit_eintraege Tabellen.
 * 
 * SICHERHEIT: RLS — nur Admin kann lesen/schreiben.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

// ─── Types ──────────────────────────────────────────────────────────────────

export type CockpitBereich = 'briefing' | 'roadmap' | 'changelog' | 'zugangsdaten' | 'marken' | 'datenbank' | 'notizen' | 'fragen';

export interface KiCockpitBereich {
  id: string;
  bereich: CockpitBereich;
  titel: string;
  beschreibung: string | null;
  icon: string | null;
  sortierung: number;
  ist_aktiv: boolean;
}

export interface KiCockpitEintrag {
  id: string;
  bereich_id: string;
  typ: string;
  titel: string;
  inhalt: string | null;
  status: 'aktiv' | 'archiviert' | 'entwurf';
  prioritaet: number;
  meta: Record<string, any> | null;
  erstellt_von: 'admin' | 'ki_agent' | 'system';
  erstellt_am: string;
  aktualisiert_am: string;
}

// ─── useKiCockpit ──────────────────────────────────────────────────────────

export function useKiCockpit() {
  const { isAdmin } = useAuth();
  const [bereiche, setBereiche] = useState<KiCockpitBereich[]>([]);
  const [eintraege, setEintraege] = useState<KiCockpitEintrag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    if (!isAdmin) {
      setBereiche([]);
      setEintraege([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [bereicheRes, eintraegeRes] = await Promise.all([
        supabase.from('ki_cockpit_bereiche').select('*').order('sortierung'),
        supabase.from('ki_cockpit_eintraege').select('*').order('prioritaet', { ascending: false }).order('erstellt_am', { ascending: false }),
      ]);
      if (bereicheRes.error) throw bereicheRes.error;
      if (eintraegeRes.error) throw eintraegeRes.error;
      setBereiche(bereicheRes.data || []);
      setEintraege(eintraegeRes.data || []);
      setError(null);
    } catch (e: any) {
      setError(e.message);
      console.error('KI-Cockpit Fehler:', e);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Einträge für einen Bereich filtern
  const getEintraege = useCallback((bereichId: string) => {
    return eintraege.filter(e => e.bereich_id === bereichId && e.status !== 'archiviert');
  }, [eintraege]);

  // Einträge für einen Bereich-Typ filtern
  const getEintraegeByBereich = useCallback((bereich: CockpitBereich) => {
    const b = bereiche.find(x => x.bereich === bereich);
    if (!b) return [];
    return eintraege.filter(e => e.bereich_id === b.id && e.status !== 'archiviert');
  }, [bereiche, eintraege]);

  // Neuen Eintrag erstellen
  const createEintrag = useCallback(async (data: {
    bereich_id: string;
    typ: string;
    titel: string;
    inhalt?: string;
    status?: 'aktiv' | 'archiviert' | 'entwurf';
    prioritaet?: number;
    meta?: Record<string, any>;
    erstellt_von?: 'admin' | 'ki_agent' | 'system';
  }) => {
    const { error: err } = await supabase
      .from('ki_cockpit_eintraege')
      .insert({
        bereich_id: data.bereich_id,
        typ: data.typ,
        titel: data.titel,
        inhalt: data.inhalt || null,
        status: data.status || 'aktiv',
        prioritaet: data.prioritaet || 0,
        meta: data.meta || null,
        erstellt_von: data.erstellt_von || 'admin',
      });
    if (err) { toast.error(`Fehler: ${err.message}`); return false; }
    toast.success('Eintrag erstellt');
    fetchAll();
    return true;
  }, [fetchAll]);

  // Eintrag aktualisieren
  const updateEintrag = useCallback(async (id: string, updates: Partial<KiCockpitEintrag>) => {
    const { error: err } = await supabase
      .from('ki_cockpit_eintraege')
      .update({ ...updates, aktualisiert_am: new Date().toISOString() })
      .eq('id', id);
    if (err) { toast.error(`Fehler: ${err.message}`); return false; }
    toast.success('Eintrag aktualisiert');
    fetchAll();
    return true;
  }, [fetchAll]);

  // Eintrag archivieren (soft delete)
  const archiveEintrag = useCallback(async (id: string) => {
    return updateEintrag(id, { status: 'archiviert' });
  }, [updateEintrag]);

  // Eintrag löschen (hard delete)
  const deleteEintrag = useCallback(async (id: string) => {
    const { error: err } = await supabase
      .from('ki_cockpit_eintraege')
      .delete()
      .eq('id', id);
    if (err) { toast.error(`Fehler: ${err.message}`); return false; }
    toast.success('Eintrag gelöscht');
    fetchAll();
    return true;
  }, [fetchAll]);

  return {
    bereiche,
    eintraege,
    loading,
    error,
    refetch: fetchAll,
    getEintraege,
    getEintraegeByBereich,
    createEintrag,
    updateEintrag,
    archiveEintrag,
    deleteEintrag,
  };
}
